import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/index.css";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/layout/AppLayout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import EquipmentPage from "@/pages/EquipmentPage";
import DocumentsPage from "@/pages/DocumentsPage";
import { HotelListPage, HotelPKSPage } from "@/pages/HotelPages";
import AuditLogPage from "@/pages/AuditLogPage";
import { SuratPilotMasukPage, SuratPilotKeluarPage, SuratFAMasukPage, SuratFAKeluarPage } from "@/pages/SuratPages";
import { ParamedicVendorPage, LaporanPreflightPage } from "@/pages/PreflightPages";
import { OFNoticePage, OFCNoticePage, OFInfoPage, OFAInfoPage, OFBInfoPage, OFCInfoPage, OFTInfoPage } from "@/pages/NoticeInfoPages";
import { InventarisOFPage, InventarisOFCPage, UangKasPage, JKTOFDashboardPage } from "@/pages/JktofPages";
import { SuratMasukPage, SuratKeluarPage } from "@/pages/SuratUnifiedPage";
import OrganizationPage from "@/pages/OrganizationPage";
import UserManagementPage from "@/pages/UserManagementPage";

const MODULE_BY_PATH = {
  "/perlengkapan/pilot": "pilot",
  "/perlengkapan/fa": "fa",
  "/perlengkapan/medis": "medical",
  "/dokumen/1": "document", "/dokumen/2": "document", "/dokumen/3": "document", "/dokumen/4": "document",
  "/surat/pilot-masuk": "surat_pilot", "/surat/pilot-keluar": "surat_pilot",
  "/surat/fa-masuk": "surat_fa", "/surat/fa-keluar": "surat_fa",
  "/hotel/pks": "hotel", "/hotel/list": "hotel",
  "/preflight/vendor": "preflight", "/preflight/laporan": "preflight",
  "/notice/of": "notice_of", "/notice/ofc": "notice_ofc",
  "/info/of": "info_of", "/info/ofc": "info_ofc",
  "/jktof": "jktof", "/jktof/inventaris-of": "jktof", "/jktof/inventaris-ofc": "jktof", "/jktof/kas": "jktof",
};

function ModuleGuard({ children, module }) {
  const { hasModule } = useAuth();
  if (module && !hasModule(module)) return <Navigate to="/dashboard" replace />;
  return children;
}
function AdminGuard({ children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin()) return <Navigate to="/dashboard" replace />;
  return children;
}

const PILOT_CATS = ["BAR","BAR 1","BAR 2","BAR 3","BAR 4","Wings","Seragam","Dasi","Topi","Log Book"];
const FA_CATS = ["Seragam","Sepatu","Hijab","Koper","Hand Bag","Travel Bag","Cabin Sandal"];
const MED_CATS = ["Alat","Consumable"];

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Memuat...</div>;
  if (!user || user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  return <AppLayout>{children}</AppLayout>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

      <Route path="/perlengkapan/pilot" element={<ProtectedRoute><ModuleGuard module="pilot"><EquipmentPage resource="pilot-equipment" title="Perlengkapan Pilot" moduleKey="pilot" categories={PILOT_CATS} testidPrefix="pilot" /></ModuleGuard></ProtectedRoute>} />
      <Route path="/perlengkapan/fa" element={<ProtectedRoute><ModuleGuard module="fa"><EquipmentPage resource="fa-equipment" title="Perlengkapan Flight Attendant" moduleKey="fa" categories={FA_CATS} testidPrefix="fa" /></ModuleGuard></ProtectedRoute>} />
      <Route path="/perlengkapan/medis" element={<ProtectedRoute><ModuleGuard module="medical"><EquipmentPage resource="medical-equipment" title="Perlengkapan Medis" moduleKey="medical" categories={MED_CATS} hasCondition testidPrefix="medical" /></ModuleGuard></ProtectedRoute>} />

      <Route path="/dokumen/1" element={<ProtectedRoute><ModuleGuard module="document"><DocumentsPage level={1} /></ModuleGuard></ProtectedRoute>} />
      <Route path="/dokumen/2" element={<ProtectedRoute><ModuleGuard module="document"><DocumentsPage level={2} /></ModuleGuard></ProtectedRoute>} />
      <Route path="/dokumen/3" element={<ProtectedRoute><ModuleGuard module="document"><DocumentsPage level={3} /></ModuleGuard></ProtectedRoute>} />
      <Route path="/dokumen/4" element={<ProtectedRoute><ModuleGuard module="document"><DocumentsPage level={4} /></ModuleGuard></ProtectedRoute>} />

      <Route path="/surat/pilot-masuk" element={<ProtectedRoute><ModuleGuard module="surat_pilot"><SuratPilotMasukPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/surat/pilot-keluar" element={<ProtectedRoute><ModuleGuard module="surat_pilot"><SuratPilotKeluarPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/surat/fa-masuk" element={<ProtectedRoute><ModuleGuard module="surat_fa"><SuratFAMasukPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/surat/fa-keluar" element={<ProtectedRoute><ModuleGuard module="surat_fa"><SuratFAKeluarPage /></ModuleGuard></ProtectedRoute>} />

      <Route path="/hotel/pks" element={<ProtectedRoute><ModuleGuard module="hotel"><HotelPKSPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/hotel/list" element={<ProtectedRoute><ModuleGuard module="hotel"><HotelListPage /></ModuleGuard></ProtectedRoute>} />

      <Route path="/preflight/vendor" element={<ProtectedRoute><ModuleGuard module="preflight"><ParamedicVendorPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/preflight/laporan" element={<ProtectedRoute><ModuleGuard module="preflight"><LaporanPreflightPage /></ModuleGuard></ProtectedRoute>} />

      <Route path="/notice/of" element={<ProtectedRoute><ModuleGuard module="notice_of"><OFNoticePage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/notice/ofc" element={<ProtectedRoute><ModuleGuard module="notice_ofc"><OFCNoticePage /></ModuleGuard></ProtectedRoute>} />

      <Route path="/info/of" element={<ProtectedRoute><ModuleGuard module="info_of"><OFInfoPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/info/ofa" element={<ProtectedRoute><ModuleGuard module="info_ofa"><OFAInfoPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/info/ofb" element={<ProtectedRoute><ModuleGuard module="info_ofb"><OFBInfoPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/info/ofc" element={<ProtectedRoute><ModuleGuard module="info_ofc"><OFCInfoPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/info/oft" element={<ProtectedRoute><ModuleGuard module="info_oft"><OFTInfoPage /></ModuleGuard></ProtectedRoute>} />

      <Route path="/jktof" element={<ProtectedRoute><ModuleGuard module="jktof_of"><JKTOFDashboardPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/jktof/inventaris-of" element={<ProtectedRoute><ModuleGuard module="jktof_of"><InventarisOFPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/jktof/inventaris-ofc" element={<ProtectedRoute><ModuleGuard module="jktof_ofc"><InventarisOFCPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/jktof/kas" element={<ProtectedRoute><ModuleGuard module="jktof_cash"><UangKasPage /></ModuleGuard></ProtectedRoute>} />
      <Route path="/jktof/surat/masuk" element={<ProtectedRoute><SuratMasukPage /></ProtectedRoute>} />
      <Route path="/jktof/surat/keluar" element={<ProtectedRoute><SuratKeluarPage /></ProtectedRoute>} />

      <Route path="/audit-log" element={<ProtectedRoute><AdminGuard><AuditLogPage /></AdminGuard></ProtectedRoute>} />
      <Route path="/system/organization" element={<ProtectedRoute><AdminGuard><OrganizationPage /></AdminGuard></ProtectedRoute>} />
      <Route path="/system/users" element={<ProtectedRoute><AdminGuard><UserManagementPage /></AdminGuard></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
